/**
 * PatientSearchSection Component
 * Handles individual patient search and addition functionality
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useClickOutside } from '@/hooks/useClickOutside';

interface PatientSearchSectionProps {
  // State
  patientSearchTerm: string;
  searchResults: string[];
  selectedPatientForAdding: string;
  filteredPatientsCount: number;
  numberOfPatientsToShow: string;
  isDuplicateMode: boolean;
  isLoading: boolean;
  selectedPatients: number[];
  
  // Actions
  onPatientSearchChange: (value: string) => void;
  onSelectPatientFromDropdown: (searchString: string) => void;
  onAddPatientFromSearch: () => void;
  onNumberOfPatientsChange: (value: string) => void;
  onShowPatientList: () => void;
}

export const PatientSearchSection: React.FC<PatientSearchSectionProps> = ({
  patientSearchTerm,
  searchResults,
  selectedPatientForAdding,
  filteredPatientsCount,
  numberOfPatientsToShow,
  isDuplicateMode,
  isLoading,
  selectedPatients,
  onPatientSearchChange,
  onSelectPatientFromDropdown,
  onAddPatientFromSearch,
  onNumberOfPatientsChange,
  onShowPatientList
}) => {
  // Handle click outside to close patient search dropdown
  const patientDropdownRef = useClickOutside<HTMLDivElement>(() => {
    if (searchResults.length > 0) {
      // Clear search results to close dropdown
      onPatientSearchChange('');
    }
  });

  return (
    <div className="space-y-6">
      {/* Patient Search */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search Patient by Name, EMR, DOB, Mobile/Home Number
        </label>
        <div className="flex space-x-2 relative">
          <div className="flex-1 relative" ref={patientDropdownRef}>
            <Input
              placeholder="Search for patients..."
              value={patientSearchTerm}
              onChange={(e) => onPatientSearchChange(e.target.value)}
              className="w-full"
            />
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => onSelectPatientFromDropdown(result)}
                  >
                    <div className="text-sm text-gray-900">{result}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            type="button"
            onClick={onAddPatientFromSearch}
            disabled={!selectedPatientForAdding}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6"
          >
            ADD
          </Button>
        </div>
      </div>

      {/* Patient Display Controls */}
      <div className="flex items-center space-x-4 mt-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter Number of Patients To Be Assigned*
          </label>
          <Input
            type="number"
            placeholder={isDuplicateMode ? "20" : "500"}
            value={numberOfPatientsToShow}
            onChange={(e) => onNumberOfPatientsChange(e.target.value)}
            className="w-32"
            disabled={filteredPatientsCount === 0}
            max={filteredPatientsCount}
          />
          {filteredPatientsCount > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Total: {filteredPatientsCount} patients available
            </div>
          )}
        </div>
        
        {/* Show Patient List Button - Hidden in duplicate mode */}
        {!isDuplicateMode && (
          <div className="flex items-end">
            <Button
              onClick={onShowPatientList}
              disabled={
                selectedPatients.length === 0 ||
                !numberOfPatientsToShow.trim() ||
                parseInt(numberOfPatientsToShow) <= 0 ||
                parseInt(numberOfPatientsToShow) > filteredPatientsCount ||
                isLoading ||
                filteredPatientsCount === 0
              }
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2"
            >
              Show Patient List
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};